import { components } from "@appretio/api";
import {
  percentFormatter,
  percentScaledValueParser,
  semanticThemeTokens
} from "@appretio/ui-kit";
import {
  CellClassParams,
  EditableCallbackParams,
  GridApi,
  ValueFormatterParams,
  ValueParserParams,
  ValueSetterParams
} from "ag-grid-enterprise";
import type { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import { isAlgorithmicProducer } from "../../../utils/algorithmicProducer";
import {
  formatDecimals,
  isPercentageMetric,
  percentageDecimals
} from "../../../utils/percentageMetrics";
import { WEEKLY_KEYS } from "../../../utils/timePeriodUtils";
import {
  getCurrentFinancialHalfYear,
  getCurrentFinancialQuarter,
  getISOWeek
} from "./calendarUtils";
import {
  INTAKE_COMPONENT_METRICS,
  type IntakeSource,
  isEditableIntakeSource,
  isIntakeMetric
} from "./intakeUtils";
import { baseNumberFormatter } from "./numberUtils";

type TransactionalGridData = components["schemas"]["TransactionalGridData"];

const MILLISECONDS_PER_WEEK = 7 * 24 * 60 * 60 * 1000; // 604,800,000

/**
 * A cube's week window (``dim_cube.year_week_min..year_week_max``, inclusive)
 * as ``CubeVersion`` ships it. Reads pad to it and the save rejects outside
 * it, so the grid greys out cells beyond it.
 */
export interface CubeWindow {
  yearWeekMin: number;
  yearWeekMax: number;
}

/**
 * The row gate for a save cube without aggregation levels for the context:
 * the save refuses that block's edits, so its rows lock. ``undefined`` when
 * the cube holds both blocks.
 */
export function cubeHoldsRowPredicate(
  holdsSales: boolean,
  holdsIntake: boolean
): ((row: { metric_name: string }) => boolean) | undefined {
  if (holdsSales && holdsIntake) return undefined;
  return function isHeld(row) {
    return isIntakeMetric(row.metric_name) ? holdsIntake : holdsSales;
  };
}

/**
 * Drop the weekly cells of ``rows`` outside ``window`` (inclusive), keeping the
 * rows. A week the mapping does not know counts as outside, as
 * ``isWithinCubeWindow`` reads it. An infinite bound cuts one side only: the
 * recompute frame ends at the cube window but keeps the history before it,
 * and the item modal's save starts at the first open week.
 */
export function dropWeeksOutsideWindow(
  rows: TransactionalGridData[],
  metaData: ForecastMetaDataType,
  window: CubeWindow
): TransactionalGridData[] {
  return rows.map(function cut(row) {
    const inside: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      if (!WEEKLY_KEYS.includes(key)) {
        inside[key] = value;
        continue;
      }
      // One rule with the editability gate: the save keeps exactly the weeks it opens.
      if (isWithinCubeWindow(metaData, row.fiscal_year_name, key, window)) {
        inside[key] = value;
      }
    }
    return inside as TransactionalGridData;
  });
}

/**
 * The recompute frame: ``rows`` cut past the cube window's end. The weeks
 * before it stay, since the history seeds the stock roll. No window, no cut.
 */
export function cutPastWindowEnd(
  rows: TransactionalGridData[],
  metaData: ForecastMetaDataType | undefined,
  window: CubeWindow | undefined
): TransactionalGridData[] {
  if (!window || !metaData) return rows;
  return dropWeeksOutsideWindow(rows, metaData, {
    yearWeekMin: Number.NEGATIVE_INFINITY,
    yearWeekMax: window.yearWeekMax
  });
}

/**
 * Whether every fiscal week backing a period column of ``fiscalYear`` lies in
 * ``window``. Backing weeks come from ``weekPeriodMapping``, their
 * ``year_week`` from ``weekYearWeekMapping``; a week missing from either map
 * counts as outside — the save rejects out-of-window weeks, so unknown must
 * not read as editable.
 */
export function isWithinCubeWindow(
  metaData: ForecastMetaDataType,
  fiscalYear: string,
  period: string,
  window: CubeWindow
): boolean {
  const weeks = backingWeeks(metaData, fiscalYear, period);
  return (
    weeks.length > 0 &&
    weeks.every(function inWindow(weekKey) {
      const yearWeek = metaData.weekYearWeekMapping[`${fiscalYear}${weekKey}`];
      return (
        yearWeek !== undefined &&
        yearWeek >= window.yearWeekMin &&
        yearWeek <= window.yearWeekMax
      );
    })
  );
}

/**
 * The weeks a forecast page cell may write: ``window`` (the save cube's, or
 * every week without one) from the forecast boundary on. As a cube window it
 * opens a period only when every week under it is open; the wall clock alone
 * would open a PTG whose first week already holds actuals.
 */
export function openFromForecastBoundary(
  window: CubeWindow | undefined,
  forecastWeekFrom: number | undefined
): CubeWindow | undefined {
  if (forecastWeekFrom === undefined) return window;
  return {
    yearWeekMin: Math.max(
      window?.yearWeekMin ?? Number.NEGATIVE_INFINITY,
      forecastWeekFrom
    ),
    yearWeekMax: window?.yearWeekMax ?? Number.POSITIVE_INFINITY
  };
}

/** The ``W##`` keys a period column (``W##`` / ``M##`` / ``Q#`` / ``H#`` / ``PTG`` / ``TTL``) rolls up. */
export function backingWeeks(
  metaData: ForecastMetaDataType,
  fiscalYear: string,
  period: string
): string[] {
  if (period.startsWith("W")) return [period];
  return WEEKLY_KEYS.filter(function rollsInto(weekKey) {
    const periods = metaData.weekPeriodMapping[`${fiscalYear}${weekKey}`];
    if (!periods) return false;
    if (period === "TTL") return true;
    if (period === "PTG") return periods.inPtg;
    return (
      periods.month === period ||
      periods.quarter === period ||
      periods.halfYear === period
    );
  });
}

/** The row + column of a cell, as both the editable and the style callbacks carry it. */
type CellRowParams = {
  data: { fiscal_year_name: string; metric_name: string };
  column: { getColId: () => string };
};

/**
 * The fiscal year an index edit WRITES: ``createIndexValueSetter`` sends a
 * past-year ratio to the ``planningYear`` row and a future-year one to the row
 * typed on. Window checks — time and cube — gate that year, so the gate and
 * the dimming agree on which row is at stake.
 *
 * The split runs against the caller's ``planningYear``, not the current fiscal
 * year: the range-plan modal (DAS-6693) can anchor on another year, and a row
 * between the two is otherwise misclassified as future by calendar offset
 * alone. Mirrors ``isFutureYear`` in indexUtils.ts.
 */
function indexWriteYear(
  fiscalYearName: string,
  planningYear: string,
  metaData: ForecastMetaDataType
): string {
  const planningYearOffset = metaData.yearMapping[planningYear] ?? 0;
  return metaData.yearMapping[fiscalYearName] >= planningYearOffset
    ? fiscalYearName
    : planningYear;
}

/**
 * Comparator function for comparing two values. Uses the metaData to determine the sort order.
 * Used to sort 'metric_name' column in chronological order
 */
export function metricComparator(
  params: [valueA: string | null, valueB: string | null],
  metaData: ForecastMetaDataType
): number {
  const [metric1, metric2] = params;

  if (metric1 === null && metric2 === null) {
    return 0;
  }
  if (metric1 === null) {
    return -1;
  }
  if (metric2 === null) {
    return 1;
  }

  const idx1 = metaData.metricSortOrder[metric1];
  const idx2 = metaData.metricSortOrder[metric2];

  // Handle metrics not found in sort order: push to end, then sort alphabetically
  if (idx1 === undefined && idx2 === undefined) {
    return metric1 < metric2 ? -1 : metric1 > metric2 ? 1 : 0;
  }
  if (idx1 === undefined) {
    return 1;
  }
  if (idx2 === undefined) {
    return -1;
  }

  return idx1 - idx2;
}

/**
 * Sets cell value, converting null to 0 as default
 */
export function setValueOrZero(params: ValueSetterParams): boolean {
  const colId = params.column.getColId();
  params.data[colId] = params.newValue === null ? 0 : params.newValue;
  return true;
}

/** Thousand separators a spreadsheet groups digits with: comma and spaces. */
const GROUPED =
  /^(-?\d{1,3})([,\u0020\u00a0\u202f])(\d{3}(?:\2\d{3})*)(\.\d+)?$/;

/** A dot-grouped integer — `561.180` — which no rule can disambiguate. */
const DOT_GROUPED = /^-?\d{1,3}(\.\d{3})+$/;

/**
 * Parses input as a number, reverting to the previous value if invalid.
 *
 * Accepts the grouped forms a spreadsheet pastes in, so a copied "561,180"
 * lands as 561180. The separator must be consistent: "1 234,567" mixes two and
 * is rejected rather than read as 1234567.
 *
 * Dot-grouped integers are rejected outright. "561.180" is a Danish 561180 and
 * an en-US 561.18, and `Number` would silently pick 561.18 — reverting the cell
 * is the lesser harm. A plain decimal like "561.18" still parses.
 */
export function parseNumericOrRevert(params: Partial<ValueParserParams>) {
  const raw =
    typeof params.newValue === "string"
      ? params.newValue.trim()
      : params.newValue;
  if (raw === "" || raw == null || DOT_GROUPED.test(String(raw))) {
    return params.oldValue;
  }
  const grouped = String(raw).match(GROUPED);
  const value = Number(
    grouped
      ? `${grouped[1]}${grouped[3].split(grouped[2]).join("")}${grouped[4] ?? ""}`
      : raw
  );
  return Number.isNaN(value) ? params.oldValue : value;
}

/**
 * Factory returning a ValueParserFunc that parses input as a number. A blank
 * keeps the previous value; unreadable text is returned as is, for the
 * recompute to refuse by cell. Percentage-metric rows go through
 * `percentScaledValueParser`, which reads their input in the label's percent
 * units. Gated on the row: the grid's rows are heterogeneous metrics, so
 * "12.35 %" pasted onto an absolute row is not a fraction and is refused.
 *
 * A factory, not a `(params, metaData)` pair, so a column definition binds one
 * stable function identity (see the ag-grid rule on column-def references) —
 * matching `createIndexValueParser` next door.
 */
export function createMetricValueParser(metaData: ForecastMetaDataType) {
  return function parseMetricValue(params: Partial<ValueParserParams>) {
    const metric = params.data?.metric_name;
    const raw =
      typeof params.newValue === "string"
        ? params.newValue.trim()
        : params.newValue;
    if (raw === "" || raw == null) return params.oldValue;
    if (isPercentageMetric(metric, metaData.metricFormatMapping)) {
      const decimals = percentageDecimals(metric, metaData.metricFormatMapping);
      // Probe without the old value: the parser falls back to it on an
      // unreadable label, but also keeps it when the label is its own.
      const readable =
        percentScaledValueParser(
          { ...params, oldValue: undefined } as ValueParserParams,
          decimals
        ) !== undefined;
      return readable
        ? percentScaledValueParser(params as ValueParserParams, decimals)
        : String(raw);
    }
    // Unreadable input goes on to the recompute, which refuses it by cell
    // rather than keeping the old value unannounced.
    const value = parseNumericOrRevert({ ...params, oldValue: Number.NaN });
    return Number.isNaN(value) ? String(raw) : value;
  };
}

/**
 * Formats a forecast cell value. Precedence:
 * 1. A percent format string ("{:.2%}") renders the fraction as a percent
 *    label ("0.6054" → "60.54 %") with the decimals it declares; the
 *    underlying value stays a fraction, and editable ones like
 *    RETURN_VALUE_PERCENTAGE keep editing that fraction (see
 *    `baseTimePeriodProperties`).
 * 2. Otherwise, the decimals of the per-metric format string.
 * 3. Otherwise, category-based formatting.
 */
export function formatCellValueByMetric(
  params: Partial<ValueFormatterParams>,
  metaData: ForecastMetaDataType
) {
  if (params.value === null) {
    return params.value;
  }

  const { metric_name } = params.data;

  if (isPercentageMetric(metric_name, metaData.metricFormatMapping)) {
    return percentFormatter(
      params as ValueFormatterParams,
      percentageDecimals(metric_name, metaData.metricFormatMapping)
    );
  }

  // Use the decimals the per-metric format string declares, if any
  const digits = formatDecimals(metric_name, metaData.metricFormatMapping);
  if (digits !== null) {
    return baseNumberFormatter(params, digits);
  }

  // Fallback: category-based formatting
  const category = metric_name
    ? metaData.metricCategoryMapping[metric_name]
    : undefined;
  const value = Number(params.value);

  if (category === "Units") {
    return value.toFixed(0);
  }

  if (category === "Value") {
    return baseNumberFormatter(params, 0);
  }

  return value.toFixed(2);
}

/**
 * Determines if the current row is an index row
 */
function isIndexRow(
  planning_fiscal_year: string,
  params: CellRowParams,
  metaData: ForecastMetaDataType
): boolean {
  const { fiscal_year_name, metric_name } = params.data;
  const colId = params.column.getColId();

  if (
    fiscal_year_name !== planning_fiscal_year &&
    metaData.indexPositiveColumns.includes(colId) &&
    metaData.indexPositiveMetrics.includes(metric_name)
  ) {
    return true;
  }
  return false;
}

/**
 * Validates if a cell is editable based on metric, producer, and cutoff constraints.
 *
 * Decision N (range-plan): an optional ``editableRowPredicate`` lets a caller
 * narrow editability per-row beyond the metric-level ``metricEditableMapping``
 * flag (e.g. NIS3-only on the range-plan item-level modal). The predicate is
 * ANDed with the existing rules — it can only further restrict, never widen.
 * Forecast's own callers don't pass it, so behaviour is unchanged.
 */
function isCellEditAllowed(
  agGridParams: EditableCallbackParams,
  appParams: {
    editable_producer: string | undefined;
    cutoff_year?: string;
    cutoff_week?: string;
    intake?: IntakeSource;
    editable_row_predicate?: (row: { metric_name: string }) => boolean;
    editable_metrics_override?: ReadonlySet<string>;
    editable_time_window_predicate?: (cell: {
      metric_name: string;
      producer_type: string;
      fiscal_year_name: string;
      colId: string;
    }) => boolean;
  },
  metaData: ForecastMetaDataType
): boolean {
  const { metric_name, fiscal_year_name, producer_type } = agGridParams.data;

  if (
    INTAKE_COMPONENT_METRICS.has(metric_name) &&
    !isEditableIntakeSource(appParams.intake)
  ) {
    return false;
  }

  // Decision N: range plan plans by NIS3 (decision G) which the cube's
  // ``metricEditableMapping`` marks as read-only because the forecast
  // page plans by GS1/intake. An optional caller-supplied whitelist
  // overrides ``metricEditableMapping`` for the metric check only;
  // every other gate (producer, row predicate, time window) still
  // applies. Forecast / budget leave this undefined → behaviour
  // unchanged.
  if (appParams.editable_metrics_override) {
    if (!appParams.editable_metrics_override.has(metric_name)) {
      return false;
    }
  } else if (!metaData.metricEditableMapping[metric_name]) {
    return false;
  }

  // Check producer permission. ``undefined`` means "no producer is
  // editable" (e.g. frozen budget cube) — reject every row.
  if (
    appParams.editable_producer === undefined ||
    producer_type !== appParams.editable_producer
  ) {
    return false;
  }

  // Optional per-row narrowing (decision N). Lets the range-plan modal
  // restrict edits to NIS3-only without forking the forecast grid.
  if (
    appParams.editable_row_predicate &&
    !appParams.editable_row_predicate(agGridParams.data)
  ) {
    return false;
  }

  // Check cutoff constraints
  const { cutoff_year, cutoff_week } = appParams;
  if (cutoff_year != null) {
    if (cutoff_year < fiscal_year_name) {
      return false;
    }

    if (cutoff_year == fiscal_year_name && cutoff_week != null) {
      if (cutoff_week < agGridParams.column.getColId()) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Validates if a cell is editable based on time window
 */
function isEditableWithinTimeWindow(
  metaData: ForecastMetaDataType,
  fiscalYear: string,
  period: string
): boolean {
  // Lookup the first day in the selected period (from Python-defined map).
  const key = `${fiscalYear}${period}`;
  const firstDay = metaData.editCalendarMapping[key];

  if (!firstDay) {
    return false;
  }

  const firstDayTimestamp = Date.parse(firstDay);
  if (isNaN(firstDayTimestamp)) {
    return false;
  }

  // Enable edit while current time (now) is before the end of the edit window:
  // the first day of the period plus one week.
  return Date.now() < firstDayTimestamp + MILLISECONDS_PER_WEEK;
}

/**
 * Determines if a forecast cell is editable
 */
export function isForecastCellEditable(
  agGridParams: EditableCallbackParams,
  appParams: {
    editable_producer: string | undefined;
    intake?: IntakeSource;
    editable_row_predicate?: (row: { metric_name: string }) => boolean;
    editable_metrics_override?: ReadonlySet<string>;
    editable_time_window_predicate?: (cell: {
      metric_name: string;
      producer_type: string;
      fiscal_year_name: string;
      colId: string;
    }) => boolean;
    cube_window?: CubeWindow;
  },
  metaData: ForecastMetaDataType
): boolean {
  if (!isCellEditAllowed(agGridParams, appParams, metaData)) {
    return false;
  }
  const { fiscal_year_name } = agGridParams.data;
  const period = agGridParams.colDef.field;

  if (!period) {
    return false;
  }
  // The cube window gates ahead of the (replaceable) time-window rule: a
  // caller's own rule widens the horizon, never the cube.
  if (
    appParams.cube_window &&
    !isWithinCubeWindow(
      metaData,
      fiscal_year_name,
      period,
      appParams.cube_window
    )
  ) {
    return false;
  }
  // Decision N: when the caller supplies its own time-window rule
  // (e.g. range-plan's ``year_week >= week_from``) it replaces the
  // forecast-page default of "current week only". Other gates above
  // (intake / metric / producer / row predicate / cutoff) still apply.
  if (appParams.editable_time_window_predicate) {
    return callTimeWindowPredicate(
      agGridParams,
      appParams.editable_time_window_predicate
    );
  }
  return isEditableWithinTimeWindow(metaData, fiscal_year_name, period);
}

/**
 * Hands the caller's window rule the four cell fields it reads.
 * `fiscalYearOverride` names the row the edit actually WRITES, where that isn't
 * the row the planner typed on (index cells — see `isIndexForecastCellEditable`).
 */
function callTimeWindowPredicate(
  agGridParams: EditableCallbackParams,
  predicate: (cell: {
    metric_name: string;
    producer_type: string;
    fiscal_year_name: string;
    colId: string;
  }) => boolean,
  fiscalYearOverride?: string
): boolean {
  return predicate({
    metric_name: agGridParams.data.metric_name,
    producer_type: agGridParams.data.producer_type,
    fiscal_year_name: fiscalYearOverride ?? agGridParams.data.fiscal_year_name,
    colId: agGridParams.column.getColId()
  });
}

/**
 * Determines if a forecast cell is editable, when plan by INDEX is enabled
 */
export function isIndexForecastCellEditable(
  agGridParams: EditableCallbackParams,
  appParams: {
    editable_producer: string | undefined;
    cutoff_year?: string;
    cutoff_week?: string;
    planning_year: string;
    intake?: IntakeSource;
    editable_row_predicate?: (row: { metric_name: string }) => boolean;
    editable_metrics_override?: ReadonlySet<string>;
    editable_time_window_predicate?: (cell: {
      metric_name: string;
      producer_type: string;
      fiscal_year_name: string;
      colId: string;
    }) => boolean;
    cube_window?: CubeWindow;
  },
  metaData: ForecastMetaDataType
): boolean {
  // A planning year the calendar doesn't know has no row to index against. A
  // historical one does, and stays plannable: rows after it write their own
  // row, rows before it write the anchor — and the time-window rule below
  // closes the past on both, so indexing against history reads but never
  // writes it.
  if (metaData.yearMapping[appParams.planning_year] === undefined) {
    return false;
  }

  if (!isCellEditAllowed(agGridParams, appParams, metaData)) {
    return false;
  }

  const { fiscal_year_name } = agGridParams.data;
  const period = agGridParams.colDef.field;

  if (!period) {
    return false;
  }

  // Handle index rows
  if (isIndexRow(appParams.planning_year, agGridParams, metaData)) {
    // Gate the row the edit WRITES (see the two branches below).
    const writtenYear = indexWriteYear(
      fiscal_year_name,
      appParams.planning_year,
      metaData
    );
    const isFutureRelativeToAnchor = writtenYear === fiscal_year_name;
    if (
      appParams.cube_window &&
      !isWithinCubeWindow(metaData, writtenYear, period, appParams.cube_window)
    ) {
      return false;
    }

    // A caller-supplied window replaces the forecast default here too — the
    // range-plan modal (DAS-6693) offers IDX / +/- Var, and leaving the index
    // branch on "current week only" would gate its cells by a rule the modal
    // already replaced for absolutes.
    //
    // Gate on the row the edit WRITES, which for a past year is not the row
    // typed on: `createIndexValueSetter` sends a past-year ratio to the
    // planning-year row and a future-year one to the row itself. Same split as
    // the two branches below — so a ratio can never land on a week the caller
    // holds closed.
    if (appParams.editable_time_window_predicate) {
      return callTimeWindowPredicate(
        agGridParams,
        appParams.editable_time_window_predicate,
        isFutureRelativeToAnchor ? undefined : appParams.planning_year
      );
    }

    // For future years, use fiscal year
    if (isFutureRelativeToAnchor) {
      return isEditableWithinTimeWindow(metaData, fiscal_year_name, period);
    }

    // For older years, use planning year
    return isEditableWithinTimeWindow(
      metaData,
      appParams.planning_year,
      period
    );
  }

  // Handle non-index rows
  return isForecastCellEditable(agGridParams, appParams, metaData);
}

/**
 * Applies current date line styling to table cells
 */
export function applyCurrentDateLine(
  params: CellClassParams,
  metaData: ForecastMetaDataType
): { borderLeft: string } | null {
  if (!params?.data?.metric_name) {
    return null;
  }

  const columnId = params.column.getColId();
  const borderStyle = {
    borderLeft: `3px solid ${semanticThemeTokens.dataTable.primaryColor}`
  };
  const { year: isGregorian } = params.data;

  // Highlight current week column — the fiscal column id resolves via
  // gregMapping (fiscal→calendar) to the calendar week getISOWeek() returns.
  const isWeekColumn = columnId.startsWith("W");
  const weekNumber = parseInt(metaData.gregMapping[columnId]?.slice(1), 10);

  if (isWeekColumn && !isNaN(weekNumber) && weekNumber === getISOWeek()) {
    return borderStyle;
  }

  // Highlight current quarter column
  const isQuarterColumn = ["Q", "GQ"].some((p) => columnId.startsWith(p));
  const quarterNumber = metaData.gregMapping[columnId];
  const financialQuarter = getCurrentFinancialQuarter(isGregorian, metaData);

  if (isQuarterColumn && quarterNumber === financialQuarter) {
    return borderStyle;
  }

  // Highlight current half-year column
  const isHalfYearColumn = columnId.startsWith("H");
  const halfYearNumber = metaData.gregMapping[columnId];
  const financialHalfYear = getCurrentFinancialHalfYear(isGregorian, metaData);

  if (isHalfYearColumn && halfYearNumber === financialHalfYear) {
    return borderStyle;
  }

  // Highlight current month column. Pinned to en-US because the label is
  // compared against the backend's month abbreviations, not shown to the user:
  // under "default" a Danish browser produced "aug." and never matched, so the
  // marker silently vanished outside English locales.
  const isMonthColumn = ["M", "GM"].some((p) => columnId.startsWith(p));
  const currentMonth = new Date().toLocaleString("en-US", { month: "short" });
  const monthNumber = isGregorian
    ? metaData.gregHeaders[columnId]
    : metaData.gregMapping[columnId];
  if (isMonthColumn && monthNumber === currentMonth) {
    return borderStyle;
  }

  return null;
}

/** How a grid dims a cell it does not let the planner edit. */
export const DIMMED_CELL_STYLE = {
  opacity: 0.45,
  fontStyle: "italic" as const
};

/**
 * Determines the cell style.
 * Applies base column type styles, current date line, and dims non-editable cells.
 *
 * ``indexPlanningYear`` is the anchor year in the index modes (IDX, +/- Var)
 * and undefined elsewhere. An index cell writes the anchor row for a past
 * year, so the window checks gate that row — matching
 * ``isIndexForecastCellEditable``, or the dimming would contradict it.
 *
 * ``clockReplaced`` is true when the caller replaced the wall-clock rule with
 * its own time-window predicate. With a ``cubeWindow`` as well, that window
 * alone dims: the caller's rule opens the cells the window holds (the item
 * modal's ``editable_week_from``), and a wall clock that disagrees would dim
 * open cells and leave closed ones looking editable.
 */
export function getCellStyle(
  params: CellClassParams,
  metaData: ForecastMetaDataType,
  cubeWindow?: CubeWindow,
  indexPlanningYear?: string,
  clockReplaced?: boolean
) {
  if (!params || !params.data || !params.node || !params.api) {
    return null;
  }

  // Get the base styles from column type definition
  const columnType = params.colDef.type;
  const gridOptions = params.api.getGridOption("columnTypes");
  const columnTypeKey = Array.isArray(columnType) ? columnType[0] : columnType;
  const baseStyles =
    (columnTypeKey && gridOptions?.[columnTypeKey]?.cellStyle) || {};

  const currentDateLineStyles = applyCurrentDateLine(params, metaData);

  // Dim cells that fall outside the editable time window or the cube window
  const { fiscal_year_name } = params.data;
  const period = params.column.getColId();
  const gatedYear =
    indexPlanningYear !== undefined &&
    isIndexRow(indexPlanningYear, params, metaData)
      ? indexWriteYear(fiscal_year_name, indexPlanningYear, metaData)
      : fiscal_year_name;
  // The Total is read-only in every row, so it takes the read-only look
  // throughout, as any cell outside the window does.
  const outsideWindow =
    period === "TTL" ||
    (!(clockReplaced && cubeWindow) &&
      !isEditableWithinTimeWindow(metaData, gatedYear, period)) ||
    (cubeWindow !== undefined &&
      !isWithinCubeWindow(metaData, gatedYear, period, cubeWindow));
  const editableStyles =
    fiscal_year_name && period && outsideWindow ? DIMMED_CELL_STYLE : undefined;

  return {
    ...baseStyles,
    ...currentDateLineStyles,
    ...editableStyles
  };
}

/**
 * The transactional rows of `producerType` among `rows`, filtered by year
 * relevance: weekly columns (W01–W53) and row identifiers only. The grid-free
 * core of {@link extractTransactionalGridData}, for a page that holds a
 * forecast's rows itself.
 */
export function extractTransactionalRows(
  rows: Iterable<Record<string, unknown>>,
  producerType: string,
  yearMapping?: Record<string, number>
): TransactionalGridData[] {
  const extracted: TransactionalGridData[] = [];
  for (const data of rows) {
    if (data.producer_type !== producerType) continue;
    if (yearMapping) {
      const yearOffset = yearMapping[String(data.fiscal_year_name)];
      if (yearOffset === undefined || yearOffset < -1) continue;
    }
    const row: Record<string, unknown> = {};
    for (const key of ["metric_name", "fiscal_year_name", "producer_type"]) {
      row[key] = data[key];
    }
    for (const key of WEEKLY_KEYS) {
      if (data[key] != null) {
        row[key] = data[key];
      }
    }
    extracted.push(row as TransactionalGridData);
  }
  return extracted;
}

/**
 * Extracts transactional grid data from the AG Grid, filtered by producer type
 * and year relevance (`extractTransactionalRows`).
 */
export function extractTransactionalGridData(
  api: GridApi,
  producerType: string,
  yearMapping?: Record<string, number>
): TransactionalGridData[] {
  const rows: Record<string, unknown>[] = [];
  api.forEachNode((node) => {
    if (node.data) rows.push(node.data);
  });
  return extractTransactionalRows(rows, producerType, yearMapping);
}

/**
 * Extracts transactional grid data grouped by producer type, excluding the
 * DS/analytics producer. Used for the multi-producer intake toggle payload.
 */
export function extractTransactionalGridDataByProducer(
  api: GridApi,
  yearMapping?: Record<string, number>
): Record<string, TransactionalGridData[]> {
  const grouped: Record<string, TransactionalGridData[]> = {};

  api.forEachNode((node) => {
    const data = node.data;
    if (!data) return;

    const producerType: string = data.producer_type;
    // Algorithmic rows are view-only page state: recomputing intake KPIs
    // for them would hand the grid rows for a producer nothing else knows.
    if (
      !producerType ||
      producerType === "ds" ||
      isAlgorithmicProducer(producerType)
    ) {
      return;
    }

    if (yearMapping) {
      const yearOffset = yearMapping[data.fiscal_year_name];
      if (yearOffset === undefined || yearOffset < -1) return;
    }

    const row: Record<string, unknown> = {};
    for (const key of ["metric_name", "fiscal_year_name", "producer_type"]) {
      row[key] = data[key];
    }
    for (const key of WEEKLY_KEYS) {
      if (data[key] != null) {
        row[key] = data[key];
      }
    }

    const list = grouped[producerType] ?? (grouped[producerType] = []);
    list.push(row as TransactionalGridData);
  });

  return grouped;
}
